import { useState, useEffect } from "react"
import {
  generateTRCCode,
  captureKeyEvent,
  addBreadcrumb,
  getSentryUserContext,
} from "helpers/sentryMonitoring"
import ModalCustom from "components/ModalCustom"
import InputDefault from "components/Input/InputDefault"
import Button from "components/Button/Button"
import { useBoolean } from "helpers/hooks"
import { encryptionController } from "controllers/EncryptionController"
import { getEncryptedKeyMiddleware } from "pages/auth/services/api"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { ReactComponent as IconHasKey } from "assets/images/icons/icon-checked-build-active.svg"
import { GetKeysType } from "types"
import {
  clearLocalStorage,
  getUserEmail,
  setUserVaultKey,
} from "helpers/local_storage"
import { umamiTracking } from "helpers/utils"
import { EVENT } from "constants/events"

interface Props {
  onSuccess: () => void
}

type Status = "recovery" | "success" | "error"

export const VaultKeyRecoveryModal = ({ onSuccess }: Props) => {
  const [status, setStatus] = useState<Status>("recovery")
  const [password, setPassword] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const [errorCode, setErrorCode] = useState("")
  const isLoading = useBoolean(false)
  const copied = useBoolean(false)

  useEffect(() => {
    if (!copied.value) return
    const timer = setTimeout(() => copied.setValue(false), 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copied.value])

  useEffect(() => {
    if (status !== "success") return
    const timer = setTimeout(() => onSuccess(), 2000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const handleConfirm = async () => {
    if (!password.trim() || isLoading.value) return
    isLoading.setValue(true)
    setErrorMsg("")
    addBreadcrumb(
      "key-recovery",
      "Vault key recovery attempt started",
      "info",
      {
        key_type: "vault",
      }
    )

    try {
      const { generateMasterKeyAndPasswordHashed } = encryptionController()
      const email = getUserEmail()
      const { masterKey } = await generateMasterKeyAndPasswordHashed(
        password,
        email
      )
      const { data: keys } = await getEncryptedKeyMiddleware({
        type: GetKeysType.VaultKey,
      })
      const encryptedVaultKey = keys[0]?.encrypted_key

      const vaultKey = (await encryptionGCMHelper.decryptContext(
        masterKey,
        encryptedVaultKey
      )) as string
      if (
        vaultKey !== encryptedVaultKey &&
        encryptionController().isValidKeyEncryption(vaultKey)
      ) {
        setUserVaultKey(vaultKey)
        umamiTracking(EVENT.KEY_DECRYPT.VAULT_SUCCESS, {})
        setStatus("success")
        return
      }
      addBreadcrumb("key-recovery", "Vault key recovery failed", "warning")
      const code = generateTRCCode("KEY")
      captureKeyEvent(`Vault key recovery failed - ${code}`, {
        error_category: "KEY",
        error_type: "VAULT_KEY_RECOVERY_FAILURE",
        ...getSentryUserContext(),
        error_code: code,
        step_trail: "PageLayout → VaultKeyRecoveryModal → recovery failed",
        key_type: "vault",
      })
      setErrorCode(code)
      setStatus("error")
      umamiTracking(EVENT.KEY_DECRYPT.VAULT_FAILURE, { error: code })
    } catch {
      setErrorMsg("Incorrect password. Please try again.")
      umamiTracking(EVENT.KEY_DECRYPT.VAULT_FAILURE, { error: "incorrect_password" })
    } finally {
      isLoading.setValue(false)
    }
  }

  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") handleConfirm()
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(errorCode)
    copied.setValue(true)
  }

  const handleLogout = () => {
    clearLocalStorage()
    pushTo(PATH.login)
  }

  const handleTryAgain = () => {
    setPassword("")
    setErrorMsg("")
    setStatus("recovery")
  }
  if (status === "success") {
    return (
      <ModalCustom
        styleLayout={{
          backgroundColor: "#222222cc",
        }}
        widthModal={440}
        label="Session Restored"
        styleHeaderModal={{ height: "auto", paddingTop: 16 }}
        bodyChildren={
          <div className="flex flex-col items-center gap-y-3 py-4">
            <IconHasKey width={35} height={35} />
            <p className="text-[15px] font-semibold text-[#222222]">
              Session Restored!
            </p>
            <p className="text-[13px] text-[#555555] text-center leading-[20px]">
              Your session has been successfully verified.
            </p>
          </div>
        }
      />
    )
  }
  if (status === "error") {
    return (
      <ModalCustom
        widthModal={440}
        styleLayout={{
          backgroundColor: "#222222cc",
        }}
        label="We Need a Quick Retry"
        styleHeaderModal={{ height: "auto", paddingTop: 16 }}
        bodyChildren={
          <div className="flex flex-col gap-y-4 pb-2">
            <p className="text-[14px] text-[#222222] font-medium leading-[22px]">
              We could not verify your session this time.
            </p>
            <div className="flex items-center gap-x-2 bg-[#F5F5F5] rounded-md px-3 py-2">
              <span className="text-[14px] font-mono font-semibold text-[#222222] flex-1">
                Error code: {errorCode}
              </span>
              <button
                className="text-[12px] text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                onClick={handleCopy}
              >
                {copied.value ? "Copied!" : "Copy"}
              </button>
            </div>
            <p className="text-[13px] text-[#555555] leading-[20px]">
              Please share this code with support so we can help you faster.
            </p>
            <div className="flex flex-col gap-y-2 mt-2">
              <div>
                <Button
                  widthBtn="100%"
                  title="Log out & Try Again"
                  colorBtn="yellow"
                  onClick={handleLogout}
                />
              </div>
              <div>
                <Button
                  widthBtn="100%"
                  title="Try Again"
                  colorBtn="white"
                  onClick={handleTryAgain}
                />
              </div>
            </div>
          </div>
        }
      />
    )
  }

  return (
    <ModalCustom
      styleLayout={{
        backgroundColor: "#222222cc",
      }}
      widthModal={440}
      label="Session Needs Verification"
      styleHeaderModal={{ height: "auto", paddingTop: 16 }}
      bodyChildren={
        <div className="flex flex-col gap-y-4 pb-2">
          <p className="text-[14px] text-[#555555] leading-[22px]">
            We need to quickly verify your account before continuing. Please
            enter your password to keep using the app.
          </p>
          <div>
            <InputDefault
              type="password"
              isPassword
              placeholder="Password"
              value={password}
              onChange={(e) =>
                setPassword((e.target as HTMLInputElement).value)
              }
              onKeyPress={onKeyPress}
            />
            {errorMsg && (
              <p className="text-[12px] text-red-500 mt-1">{errorMsg}</p>
            )}
          </div>
          <Button
            widthBtn="100%"
            title={isLoading.value ? "Verifying..." : "Confirm"}
            onClick={handleConfirm}
            disabled={!password.trim() || isLoading.value}
            isDisabledBtn
          />
        </div>
      }
    />
  )
}
